import { apiClient } from './ApiClient';

export interface CheckoutResponse {
  success: boolean;
  url?: string;
  message?: string;
  redirectUrl?: string;
}

export interface SubscriptionStatus {
  plan: string;
  status: string;
  stripeSubscription?: {
    id: string;
    status: string;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
  };
}

export interface CustomerPortalResponse {
  success: boolean;
  url?: string;
  isCheckout?: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  monthly: {
    priceId: string;
    amount: number;
    interval: string;
    formattedPrice: string;
  };
  annual: {
    priceId: string;
    amount: number;
    interval: string;
    formattedPrice: string;
  };
}

export interface PlansResponse {
  success: boolean;
  plans: SubscriptionPlan[];
}

class BillingService {
  private apiClient = apiClient;

  async createCheckoutSession(
    plan: 'starter' | 'pro' | 'premium',
    interval: 'monthly' | 'annual' = 'monthly',
  ): Promise<CheckoutResponse> {
    try {
      // Determine cancel URL based on authentication context
      // If we're in an authenticated context (settings page), go back to settings
      // Otherwise, go to pricing page for unauthenticated users
      const isAuthenticatedContext = window.location.pathname.includes('/app/');
      const cancelUrl = isAuthenticatedContext
        ? `${window.location.origin}/app/settings`
        : `${window.location.origin}/#pricing`;

      // Use different endpoints based on authentication context
      const endpoint = isAuthenticatedContext
        ? '/billing/checkout'
        : '/billing/checkout/guest';

      const response = await this.apiClient.post(endpoint, {
        plan,
        interval,
        successUrl: `${window.location.origin}/app/settings?plan=${plan}&success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  async confirmCheckout(
    sessionId: string,
  ): Promise<{ success: boolean; plan: string; status: string }> {
    try {
      const response = await this.apiClient.post('/billing/confirm', {
        sessionId,
      });
      return response.data;
    } catch (error) {
      console.error('Error confirming checkout session:', error);
      throw error;
    }
  }

  async createCustomerPortalSession(): Promise<CustomerPortalResponse> {
    try {
      const response = await this.apiClient.post('/billing/portal');
      return response.data;
    } catch (error) {
      console.error('Error creating customer portal session:', error);
      throw error;
    }
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      const response = await this.apiClient.get('/billing/status');
      return response.data;
    } catch (error) {
      console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  async getSubscriptionPlans(): Promise<PlansResponse> {
    try {
      const response = await this.apiClient.get('/billing/plans');
      return response.data;
    } catch (error) {
      console.error('Error getting subscription plans:', error);
      throw error;
    }
  }
}

export default new BillingService();
